// Source of truth for retreat room pricing + deposits.
//
// Values live ONLY in the environment, read through `app/lib/fees.ts` — the same
// discipline as every other price on the site. They used to be hardcoded here AND
// again in the booking form, which is how `/retreats` came to advertise £3,000 for
// a couple's room while checkout billed £2,800.
//
// Deliberately functions rather than a module-level `ROOM_PRICING` const: `getFee`
// throws on a missing var, and a throw at import time would take down the whole
// booking page and the API route rather than surfacing as a clean error.
//
// The deposit is per person, so a couple's room is two of them.

import { getFee, getFeeOptional, type FeeKey } from "@/app/lib/fees";

export type RoomTypeKey = "Single (own)" | "Shared (Couple)";

export type RoomPricing = {
  /** Full retreat price (per booking) */
  price: number;
  /** Deposit charged at checkout */
  deposit: number;
  /** Human-readable label for emails/UI */
  label: string;
  /** Whether this room type expects a second guest's details */
  isCouple: boolean;
};

/** Everything about a room type that isn't a price. Safe to read anywhere. */
const ROOM_META: Record<RoomTypeKey, { label: string; isCouple: boolean; priceFee: FeeKey; guests: number }> = {
  "Single (own)": { label: "Single (Own Room)", isCouple: false, priceFee: "retreatSingle", guests: 1 },
  "Shared (Couple)": { label: "Shared Room (Couple)", isCouple: true, priceFee: "retreatCouple", guests: 2 },
};

export const ROOM_TYPES = Object.keys(ROOM_META) as RoomTypeKey[];

export function isRoomTypeKey(value: string): value is RoomTypeKey {
  return value in ROOM_META;
}

/** Label + isCouple without touching the environment — never throws. */
export function roomMeta(key: RoomTypeKey) {
  const { label, isCouple } = ROOM_META[key];
  return { label, isCouple };
}

/**
 * Pricing for a room type. Throws if the fee env vars are missing — use this on the
 * charge path so a misconfigured deploy fails loudly instead of taking a wrong deposit.
 */
export function getRoomPricing(key: RoomTypeKey): RoomPricing {
  const meta = ROOM_META[key];
  return {
    price: getFee(meta.priceFee),
    deposit: getFee("retreatDeposit") * meta.guests,
    label: meta.label,
    isCouple: meta.isCouple,
  };
}

/**
 * Pricing for display. Returns null rather than throwing when a fee is unset, so a
 * missing env var omits a price instead of blanking the page (same reasoning as
 * `feeLabel` in app/lib/fees.ts).
 */
export function getRoomPricingOptional(key: RoomTypeKey): RoomPricing | null {
  const meta = ROOM_META[key];
  const price = getFeeOptional(meta.priceFee);
  const perPerson = getFeeOptional("retreatDeposit");
  if (price === null || perPerson === null) return null;
  return { price, deposit: perPerson * meta.guests, label: meta.label, isCouple: meta.isCouple };
}

/** Balance is due ~1 month before the retreat starts. Returns "YYYY-MM-DD". */
export function balanceDueDateFor(startDate: string): string {
  const d = new Date(`${startDate}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 10);
}
